import React, { useState, useEffect, useCallback, useRef, useMemo, forwardRef, useImperativeHandle } from "react";
import { useRouter } from "next/router";
import { observer } from "mobx-react-lite";
import cx from "classnames";
import { Checkbox, message } from "antd";
import { Hooks, Util, Request } from "@xt/xtbase";
const { useTranslation } = Hooks;
const { Big } = Util;
import moment from "moment";
import XtSvg from "@/components/Xt/Svg";
import ModalAlert from "@/components/Antd/modal/alert";
import StopModal from "./stopModal";
import CloseConfirmModal from "./closeConfirmModal";
import SharePosterModal, { type SharePosterPayload } from "./SharePosterModal";
import store from "store";
import Storage from "utils/storage";
import { get_cfd_positions, get_cfd_orders, put_cfd_positionKeep, post_cfd_cancelOrder, post_cfd_closePosition, post_cfd_closeAllPositions, post_cfd_cancelAllOrders } from "api/v4/cfd";
import useCoinMemo from "@/components/Pages/Trade/_hook/useCoinMemo";
import useCfdConfig from "../../_hook/useCfdConfig";
import useAxiosCancelFun from "hooks/useAxiosCancelFun";
import Socket from "utils/socket/public";
import { isPropTradingCfdTradeRoute } from "@/utils/cfd/isPropTradingCfdTradeRoute";
import styles from "./index.module.scss";

/** 仓位列表轮询间隔：仅做列表一致性兜底（新开/平仓），价格与收益由 socket ticker 实时更新，可适当放长以减轻接口压力 */
const POSITION_POLL_INTERVAL_MS = 30000;

type TabType = "position" | "order" | "history";

export interface PositionListRef {
  /** showLoading: 是否显示 loading（默认 true）；定时刷新传 false 做静默局部刷新，避免列表闪一下 */
  refresh: (showLoading?: boolean) => void;
}

// 提取 PositionItem 为独立组件，在组件内部直接读取实时价格，避免通过 props 传递导致重新渲染
interface PositionItemProps {
  position: any;
  rawData: any; // 原始数据，用于获取 symbol
  t: any;
  onPairClick: (symbol: string) => void;
  handleOvernightChange: (positionId: string, checked: boolean) => void;
  setStopPosition: (position: any) => void;
  setStopModalOpen: (open: boolean) => void;
  handleClosePosition: (position: any) => void;
  coinPricePrecisionMarket: number;
  getRealTimePrice: (symbol: string, apiPrice: string) => any;
  /** 是否展开显示详情（默认折叠） */
  expanded: boolean;
  onToggleExpand: () => void;
  /** 点击分享时回调，传入海报所需数据 */
  onShareClick: (payload: SharePosterPayload) => void;
}

const PositionItem = observer<PositionItemProps>(({ 
  position, 
  rawData,
  t, 
  onPairClick,
  handleOvernightChange, 
  setStopPosition, 
  setStopModalOpen,
  handleClosePosition,
  coinPricePrecisionMarket,
  getRealTimePrice,
  expanded,
  onToggleExpand,
  onShareClick,
}) => {
  // Latest price 暂时全部从接口 get_cfd_positions 的 currentPrice 获取（见 getRealTimePrice）
  const realTimePrice = (() => {
    const price = getRealTimePrice(rawData?.symbol || "", rawData?.currentPrice || "0");
    const pricePrecision = coinPricePrecisionMarket && coinPricePrecisionMarket > 0 ? coinPricePrecisionMarket : 2;
    return price.gt(0) ? price.toFixedCy(pricePrecision) : "0";
  })();

  // 根据实时价格计算收益率和收益
  const { realTimeProfitLossPercent, realTimeProfitLossValue, isNegative } = (() => {
    // 安全地转换值为数字字符串，处理可能的格式化字符串（如带逗号的数字）
    const safeParseNumber = (value: any): string => {
      if (!value || value === "--" || value === "-") return "0";
      // 移除逗号和其他非数字字符（保留小数点和负号）
      const cleaned = String(value).replace(/,/g, "").trim();
      // 检查是否为有效数字
      const num = parseFloat(cleaned);
      return isNaN(num) || !isFinite(num) ? "0" : cleaned;
    };

    try {
      const entryPriceStr = safeParseNumber(position.entryPrice);
      const currentPriceStr = safeParseNumber(realTimePrice);
      const quantityStr = safeParseNumber(position.quantity);
      const marginStr = safeParseNumber(position.margin);
      
      const entryPrice = Big(entryPriceStr);
      const currentPrice = Big(currentPriceStr);
      const quantity = Big(quantityStr);
      const margin = Big(marginStr);
      const pricePrecision = coinPricePrecisionMarket && coinPricePrecisionMarket > 0 ? coinPricePrecisionMarket : 2;

      if (entryPrice.gt(0) && currentPrice.gt(0) && quantity.gt(0)) {
        // 计算盈亏：买涨时 (当前价 - 入场价) * 数量，卖跌时 (入场价 - 当前价) * 数量
        const priceDiff = position.side === "buy" 
          ? currentPrice.minus(entryPrice) 
          : entryPrice.minus(currentPrice);
        const unrealizedPnl = priceDiff.times(quantity);
        
        // 计算收益率 = 盈亏 / 保证金 * 100%
        let profitLossPercentNum = 0;
        if (margin.gt(0)) {
          profitLossPercentNum = unrealizedPnl.div(margin).times(100).toNumber();
        }
        
        const profitLossPercent = `${profitLossPercentNum >= 0 ? "+" : ""}${profitLossPercentNum.toFixed(2)}%`;
        const profitLossValue = unrealizedPnl.gte(0) 
          ? `+${unrealizedPnl.toFixedCy(pricePrecision)}` 
          : unrealizedPnl.toFixedCy(pricePrecision);
        
        return {
          realTimeProfitLossPercent: profitLossPercent,
          realTimeProfitLossValue: profitLossValue,
          isNegative: unrealizedPnl.lt(0),
        };
      }
    } catch (error) {
      console.error("计算实时盈亏失败:", error, { position, realTimePrice });
    }
    
    // 如果无法计算，使用原始数据
    return {
      realTimeProfitLossPercent: position.profitLoss || "0.00%",
      realTimeProfitLossValue: position.profitLossValue || "+0",
      isNegative: position.isProfitLossNegative || false,
    };
  })();

  const expandTriggerAria = expanded ? "收起" : "展开";
  const quantityUnit = useMemo(() => {
    const rawSymbol = rawData?.symbol || position?.pair || "";
    const normalizedSymbol = String(rawSymbol).replace(/_cfd$/i, "");
    const base = normalizedSymbol.split(/[_/]/)[0];
    return (base || "BTC").toUpperCase();
  }, [position?.pair, rawData?.symbol]);

  const handleShare = (e: React.MouseEvent) => {
    e.stopPropagation();
    onShareClick({
      position: {
        pair: position.pair,
        side: position.side,
        leverage: position.leverage,
        entryPrice: position.entryPrice,
        quantity: position.quantity,
        margin: position.margin,
      },
      realTimePrice,
      realTimeProfitLossValue,
      isNegative,
    });
  };

  const handleCollapsedClose = (e: React.MouseEvent) => {
    e.stopPropagation();
    handleClosePosition(position);
  };

  const handlePairClick = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.stopPropagation();
    const symbol = rawData?.symbol;
    if (symbol) onPairClick(symbol);
  };

  return (
    <div className={styles.positionItem}>
      {/* 设计稿：折叠时仅一行（买涨/交易对/杠杆+收益率）+ 右侧分享+展开箭头；展开时右侧为分享+收起箭头+持仓过夜 */}
      <div className={styles.positionHeader}>
        <div
          className={styles.positionInfo}
          role="button"
          tabIndex={0}
          onClick={onToggleExpand}
          onKeyDown={(e) => e.key === "Enter" && onToggleExpand()}
          aria-label={expandTriggerAria}
        >
          <div className={styles.positionInfoTop}>
            <span className={cx(styles.sideTag, { [styles.buy]: position.side === "buy", [styles.sell]: position.side === "sell" })}>
              {position.side === "buy" ? t("cfd.buyUp") : t("cfd.buyDown")}
            </span>
            <button
              type="button"
              className={styles.pairButton}
              onClick={handlePairClick}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  handlePairClick(e);
                }
              }}
            >
              <span className={styles.pair}>{position.pair}</span>
            </button>
            <span className={styles.leverage}>{position.leverage}</span>
          </div>
          <div className={styles.profitLossRow}>
            <div className={styles.profitLoss}>
              <span className={cx(styles.profitLossValue, { [styles.negative]: isNegative })}>
                {realTimeProfitLossPercent}
              </span>
              <span className={cx(styles.profitLossAmount, { [styles.negative]: isNegative })}>
                {realTimeProfitLossValue}
              </span>
            </div>
          </div>
        </div>
        <div className={styles.headerRight}>
          
          <div className={styles.headerRightIcons}>
            
            <div
              className={styles.expandTrigger}
              onClick={onToggleExpand}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === "Enter" && onToggleExpand()}
              aria-label={expandTriggerAria}
            >
              {!expanded && <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className={styles.expandIcon}>
                <path d="M8 8.22217L12 11.3333L16 8.22217" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M8 12.6666L12 15.7777L16 12.6666" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>}
            </div>
            <button
              type="button"
              className={styles.shareBtn}
              onClick={handleShare}
              aria-label={t("cfd.share") || "Share"}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="13" viewBox="0 0 14 13" fill="none" className={styles.shareIcon}>
                <path d="M7.33358 1.33575L11.8522 4.91308L7.33358 8.49041V6.25641C6.81891 6.27775 6.00024 6.32775 6.00024 6.32775C4.50358 6.51508 3.13824 7.02108 1.96691 7.81641C2.37024 6.63108 2.92624 5.78241 3.51624 5.17775C4.19561 4.47981 5.05563 3.98448 6.00024 3.74708C6.00024 3.74708 6.82758 3.64708 7.33358 3.60175V1.33575ZM6.00024 7.67308V8.49041C6.00024 9.60508 7.28691 10.2277 8.16091 9.53575L12.6796 5.95842C12.8372 5.83359 12.9646 5.67471 13.0522 5.49369C13.1397 5.31266 13.1852 5.11417 13.1852 4.91308C13.1852 4.71199 13.1397 4.5135 13.0522 4.33248C12.9646 4.15145 12.8372 3.99258 12.6796 3.86775L8.16091 0.290415C7.28758 -0.401585 6.00024 0.221081 6.00024 1.33575V2.38041C3.64891 2.85441 0.976245 4.68375 0.213578 9.40041C0.066594 10.3419 -0.00474523 11.2936 0.00024468 12.2464C0.362667 11.417 0.860578 10.6537 1.47358 9.98775C2.65024 8.72441 4.20024 7.93175 6.00024 7.67308Z" fill="white"/>
              </svg>
            </button>
          </div>
          {expanded && (
            <div className={styles.positionOvernight} onClick={(e) => e.stopPropagation()}>
              <div className={styles.overnightToggle}>
                <Checkbox
                  checked={position.overnight}
                  onChange={(e) => handleOvernightChange(position.id, e.target.checked)}
                />
                <span className={styles.overnightLabel}>{t("cfd.overnight")}</span>
              </div>
            </div>
          )}
          {!expanded && (
            <button
              type="button"
              className={styles.collapsedCloseBtn}
              onClick={handleCollapsedClose}
            >
              {t("cfd.closePosition")}
            </button>
          )}
        </div>
      </div>

      {/* 展开后显示：持仓量、买入价、保证金、最新价、止盈止损及操作按钮 */}
      {expanded && (
        <>
          <div className={styles.positionDetails}>
            <div className={styles.detailRow}>
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>{t("cfd.positionQuantity")}({quantityUnit})</span>
                <span className={styles.detailValue}>{position.quantity}</span>
              </div>
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>{t("cfd.entryPrice")}</span>
                <span className={styles.detailValue}>{position.entryPrice}</span>
              </div>
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>{t("cfd.margin")}</span>
                <span className={styles.detailValue}>{position.margin}</span>
              </div>
            </div>
            <div className={styles.detailRow}>
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>{t("cfd.latestPrice")}</span>
                <span className={styles.detailValue}>{realTimePrice}</span>
              </div>
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>{t("cfd.stopProfitPrice")}</span>
                <span className={styles.detailValue}>{position.stopProfit}</span>
              </div>
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>{t("cfd.stopLossPrice")}</span>
                <span className={styles.detailValue}>{position.stopLoss}</span>
              </div>
            </div>
          </div>

          <div className={styles.positionActions}>
            <button
              className={styles.actionBtn}
              onClick={() => {
                setStopPosition({
                  id: position.id,
                  pair: position.pair,
                  side: position.side,
                  leverage: position.leverage,
                  entryPrice: position.entryPrice,
                  currentPrice: realTimePrice,
                  quantity: position.quantity,
                  stopProfit: position.stopProfit,
                  stopLoss: position.stopLoss,
                });
                setStopModalOpen(true);
              }}
            >
              {t("cfd.stopProfitLoss")}
            </button>
            <button
              className={styles.actionBtn}
              onClick={() => handleClosePosition(position)}
            >
              {t("cfd.closePosition")}
            </button>
          </div>

          {/* 展开后底部居中收起 icon：双线下箭头，透明度 0.56 */}
          <div
            className={styles.expandTriggerBottom}
            onClick={onToggleExpand}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === "Enter" && onToggleExpand()}
            aria-label={expandTriggerAria}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className={styles.expandIconDown}>
              <path d="M8 15.7778L12 12.6667L16 15.7778" stroke="white" strokeOpacity="0.56" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M8 11.3333L12 8.2222L16 11.3333" stroke="white" strokeOpacity="0.56" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
        </>
      )}
    </div>
  );
});

PositionItem.displayName = "PositionItem";

const PositionList = forwardRef<PositionListRef>((props, ref) => {
  const t = useTranslation();
  const router = useRouter();
  const [activeTab, setActiveTab] = useState<TabType>("position");
  const [hideOtherPairs, setHideOtherPairs] = useState(false);
  const [stopModalOpen, setStopModalOpen] = useState(false);
  const [stopPosition, setStopPosition] = useState<any>();
  const [positions, setPositions] = useState<any[]>([]);
  const [positionsRawData, setPositionsRawData] = useState<any[]>([]); // 保存原始数据，用于实时价格更新
  const [allPositions, setAllPositions] = useState<any[]>([]); // 保存所有持仓数据（不过滤）
  const [allPositionsRawData, setAllPositionsRawData] = useState<any[]>([]); // 保存所有原始数据（不过滤）
  const [orders, setOrders] = useState<any[]>([]);
  const [allOrders, setAllOrders] = useState<any[]>([]); // 保存所有订单数据（不过滤）
  const [loading, setLoading] = useState(false);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [positionCount, setPositionCount] = useState<number>(0);
  const [orderCount, setOrderCount] = useState<number>(0);
  const [closeConfirmOpen, setCloseConfirmOpen] = useState(false);
  const [closeConfirmContent, setCloseConfirmContent] = useState("");
  const [closeConfirmLoading, setCloseConfirmLoading] = useState(false);
  const [showCloseConfirmSkipNext, setShowCloseConfirmSkipNext] = useState(false);
  const [closeConfirmSkipNextChecked, setCloseConfirmSkipNextChecked] = useState(false);
  const closeConfirmCallbackRef = useRef<(() => Promise<void>) | null>(null);
  const closeConfirmSkipNextCheckedRef = useRef(false);
  const hideOtherPairsRef = useRef(hideOtherPairs);
  /** 分享海报弹窗 */
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [sharePayload, setSharePayload] = useState<SharePosterPayload | null>(null);
  /** 当前持仓默认折叠，点击展开；记录已展开的持仓 id */
  const [expandedPositionIds, setExpandedPositionIds] = useState<Set<string>>(new Set());
  const togglePositionExpand = useCallback((id: string) => {
    setExpandedPositionIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);
  
  const query = useRef<Partial<{ limit: number; page: number; symbol?: string; propTrading: boolean }>>({
    limit: 100,
    page: 1,
    propTrading: false,
  });
  
  // 更新 ref 以保持最新值
  useEffect(() => {
    hideOtherPairsRef.current = hideOtherPairs;
  }, [hideOtherPairs]);

  const { name } = store.market;
  const { isLogin } = store.user;
  const cfdPositionClosedPositionId = store.trade.cfdPositionClosedPositionId;
  const { coinQuantityPrecisionMarket, coinPricePrecisionMarket } = useCoinMemo();
  const cfdConfig = useCfdConfig();
  const { cfdTicker, tradeRecent, positionTickers } = store.cfdView;

  // 获取当前交易对的 symbol（去掉 _cfd 后缀）
  const currentSymbol = useMemo(() => {
    if (!name) return undefined;
    return name.replace(/_cfd$/i, "").toUpperCase();
  }, [name]);

  // 需要单独订阅 ticker 的「其他币对」：当前页已有 cfdTicker，只订阅仓位中非当前交易对的 symbol（小写，与 socket 事件一致）
  const positionOtherSymbols = useMemo(() => {
    const currentLower = (name || "").toLowerCase();
    const set = new Set<string>();
    allPositionsRawData.forEach((r: any) => {
      const s = (r?.symbol || "").toLowerCase();
      if (s && s !== currentLower) set.add(s);
    });
    return Array.from(set);
  }, [name, allPositionsRawData]);

  // 订阅其他币对 ticker，用于实时更新仓位最新价与收益；当前币对由 header 订阅，不重复订阅
  const positionOtherSymbolsRef = useRef<string[]>([]);
  useEffect(() => {
    const prev = positionOtherSymbolsRef.current;
    positionOtherSymbolsRef.current = positionOtherSymbols;

    const subscribe = (symbol: string) => {
      const message = { event: `ticker@${symbol}` };
      Socket.addChannel(message, (data: any) => {
        store.cfdView.updateState({
          positionTickers: { ...store.cfdView.positionTickers, [symbol]: data },
        });
      });
    };
    const unsubscribe = (symbol: string) => {
      Socket.removeChannel({ event: `ticker@${symbol}` });
    };

    positionOtherSymbols.forEach((s) => {
      if (!prev.includes(s)) subscribe(s);
    });
    const removed: string[] = [];
    prev.forEach((s) => {
      if (!positionOtherSymbols.includes(s)) {
        unsubscribe(s);
        removed.push(s);
      }
    });
    if (removed.length > 0) {
      const next = { ...store.cfdView.positionTickers };
      removed.forEach((s) => delete next[s]);
      store.cfdView.updateState({ positionTickers: next });
    }

    return () => {
      const subscribed = positionOtherSymbolsRef.current;
      subscribed.forEach((s) => unsubscribe(s));
      if (subscribed.length > 0) {
        const next = { ...store.cfdView.positionTickers };
        subscribed.forEach((s) => delete next[s]);
        store.cfdView.updateState({ positionTickers: next });
      }
      positionOtherSymbolsRef.current = [];
    };
  }, [positionOtherSymbols]);

  // Latest price：当前币对用 cfdTicker/tradeRecent；其他币对用 positionTickers（socket 订阅）；都没有时用接口 currentPrice
  const getRealTimePrice = useCallback((itemSymbol: string, apiCurrentPrice: string) => {
    const itemSymbolNorm = (itemSymbol || "").replace(/_cfd$/i, "").toUpperCase();
    if (itemSymbolNorm === currentSymbol) {
      if (cfdTicker && cfdTicker.c) return Big(cfdTicker.c);
      if (tradeRecent && tradeRecent.p) return Big(tradeRecent.p);
    }
    const key = (itemSymbol || "").toLowerCase();
    const otherPrice = positionTickers[key]?.c;
    if (otherPrice) return Big(otherPrice);
    return Big(apiCurrentPrice || 0);
  }, [currentSymbol, cfdTicker, tradeRecent, positionTickers]);

  const handlePairClick = useCallback((symbol: string) => {
    const normalizedSymbol = String(symbol || "").trim().toLowerCase();
    if (!normalizedSymbol) return;
    const targetSymbol = normalizedSymbol.endsWith("_cfd") ? normalizedSymbol : `${normalizedSymbol}_cfd`;
    const currentMarketSymbol = String(store.market.name || "").trim().toLowerCase();

    if (targetSymbol === currentMarketSymbol) {
      return;
    }

    if (
      (router.pathname || "") === "/proptrading/trade" ||
      (router.pathname || "") === "/proptrading/trade/[symbol]"
    ) {
      router.push(`/proptrading/trade/${targetSymbol}`);
      return;
    }

    router.push(`/futures/contract/${targetSymbol}`);
  }, [router]);

  const apiReqPositionArg = useMemo(() => {
    return {
      fn: get_cfd_positions,
      config: {
        params: query.current,
      },
      success: (data) => {
        const items = data?.items || [];
        
        // 更新持仓数量
        setPositionCount(items.length);
        
        // 将 API 返回的数据转换为组件需要的格式
        // 注意：不在格式化时使用实时价格，实时价格在 PositionItem 组件内部计算，避免触发重新获取数据
        const formattedPositions = items.map((item: any) => {
          const avgPrice = Big(item.avgPrice || 0);
          const apiCurrentPrice = Big(item.currentPrice || 0);
          const quantity = Big(item.quantity || 0);
          const margin = Big(item.margin || 0);
          const unrealizedPnl = Big(item.unrealizedPnl || 0);
          const takeProfitPrice = Big(item.takeProfitPrice || 0);
          const stopLossPrice = Big(item.stopLossPrice || 0);
          
          // 计算实时收益率 = 盈亏 / 保证金 * 100%
          let profitLossPercentNum = 0;
          let profitLossPercent = "0.00";
          if (margin.gt(0)) {
            profitLossPercentNum = unrealizedPnl.div(margin).times(100).toNumber();
            profitLossPercent = profitLossPercentNum.toFixed(2);
          }
          
          // 格式化交易对显示，去掉 _cfd 后缀，使用和现货一样的格式
          const pairDisplay = store.market.formatCfdName(item.symbol || "");
          
          // 获取精度
          const quantityPrecision = coinQuantityPrecisionMarket && coinQuantityPrecisionMarket > 0 ? coinQuantityPrecisionMarket : 8;
          const pricePrecision = coinPricePrecisionMarket && coinPricePrecisionMarket > 0 ? coinPricePrecisionMarket : 2;
          // 使用CFD配置的保证金精度，如果没有则使用价格精度
          const marginPrecision = cfdConfig.marginPrecision !== undefined ? cfdConfig.marginPrecision : pricePrecision;
          
          return {
            id: item.id,
            pair: pairDisplay,
            side: item.side === 1 ? "buy" : "sell", // 1=买涨, 2=卖跌
            leverage: `${item.leverage || 0}X`,
            quantity: quantity.gt(0) ? quantity.toFixedCy(quantityPrecision) : "0",
            entryPrice: avgPrice.gt(0) ? avgPrice.toFixedCy(pricePrecision) : "0",
            currentPrice: apiCurrentPrice.gt(0) ? apiCurrentPrice.toFixedCy(pricePrecision) : "0", // 使用 API 返回的价格，实时价格在组件内部计算
            margin: margin.gt(0) ? margin.toFixedCy(marginPrecision) : "0",
            profitLoss: `${profitLossPercentNum >= 0 ? "+" : ""}${profitLossPercent}%`,
            profitLossValue: unrealizedPnl.gte(0) ? `+${unrealizedPnl.toFixedCy(pricePrecision)}` : unrealizedPnl.toFixedCy(pricePrecision),
            isProfitLossNegative: unrealizedPnl.lt(0), // 标记是否为负值
            stopProfit: takeProfitPrice.gt(0) ? takeProfitPrice.toFixedCy(pricePrecision) : "--",
            stopLoss: stopLossPrice.gt(0) ? stopLossPrice.toFixedCy(pricePrecision) : "--",
            overnight: item.keep === 1, // 0=关闭, 1=开启
            // 保留原始数据，方便后续操作
            rawData: item,
          };
        });

        // 保存所有原始数据（不过滤）
        setAllPositionsRawData(items);
        setAllPositions(formattedPositions);
        
        // 根据 hideOtherPairs 过滤数据（前端过滤）
        if (hideOtherPairsRef.current && currentSymbol) {
          // 过滤出当前交易对的持仓
          const filtered = formattedPositions.filter((pos, index) => {
            const rawItem = items[index];
            if (!rawItem || !rawItem.symbol) return false;
            const itemSymbol = rawItem.symbol.replace(/_cfd$/i, "").toUpperCase();
            return itemSymbol === currentSymbol;
          });
          const filteredRaw = items.filter((rawItem) => {
            if (!rawItem || !rawItem.symbol) return false;
            const itemSymbol = rawItem.symbol.replace(/_cfd$/i, "").toUpperCase();
            return itemSymbol === currentSymbol;
          });
          setPositions(filtered);
          setPositionsRawData(filteredRaw);
          setPositionCount(filtered.length);
        } else {
          // 显示所有持仓
          setPositions(formattedPositions);
          setPositionsRawData(items);
          setPositionCount(formattedPositions.length);
        }
      },
      callback: () => setLoading(false),
    };
  }, [coinQuantityPrecisionMarket, coinPricePrecisionMarket, cfdConfig.marginPrecision, currentSymbol]);
  const apiReqPosition = useAxiosCancelFun(apiReqPositionArg);

  const handleRefresh = useCallback((showLoading = true) => {
    if (showLoading) setLoading(true);
    query.current.propTrading = isPropTradingCfdTradeRoute(router);
    apiReqPosition();
  }, [apiReqPosition, router]);

  // 根据 hideOtherPairs 过滤持仓数据（前端过滤，不重新调用接口）
  const filterPositionsByHideOtherPairs = useCallback((shouldHide: boolean, allPositionsData: any[], allRawData: any[]) => {
    if (shouldHide && currentSymbol) {
      // 过滤出当前交易对的持仓
      const filtered = allPositionsData.filter((pos, index) => {
        const rawItem = allRawData[index];
        if (!rawItem || !rawItem.symbol) return false;
        const itemSymbol = rawItem.symbol.replace(/_cfd$/i, "").toUpperCase();
        return itemSymbol === currentSymbol;
      });
      const filteredRaw = allRawData.filter((rawItem) => {
        if (!rawItem || !rawItem.symbol) return false;
        const itemSymbol = rawItem.symbol.replace(/_cfd$/i, "").toUpperCase();
        return itemSymbol === currentSymbol;
      });
      setPositions(filtered);
      setPositionsRawData(filteredRaw);
      setPositionCount(filtered.length);
    } else {
      // 显示所有持仓
      setPositions(allPositionsData);
      setPositionsRawData(allRawData);
      setPositionCount(allPositionsData.length);
    }
  }, [currentSymbol]);

  // 根据 hideOtherPairs 过滤订单数据（前端过滤，不重新调用接口）
  const filterOrdersByHideOtherPairs = useCallback((shouldHide: boolean, allOrdersData: any[]) => {
    if (shouldHide && currentSymbol) {
      // 过滤出当前交易对的订单
      const filtered = allOrdersData.filter((order) => {
        const rawItem = order.rawData;
        if (!rawItem || !rawItem.symbol) return false;
        const itemSymbol = rawItem.symbol.replace(/_cfd$/i, "").toUpperCase();
        return itemSymbol === currentSymbol;
      });
      setOrders(filtered);
      setOrderCount(filtered.length);
    } else {
      // 显示所有订单
      setOrders(allOrdersData);
      setOrderCount(allOrdersData.length);
    }
  }, [currentSymbol]);

  // 处理 hideOtherPairs 切换（乐观更新，不刷新列表）
  const handleHideOtherPairsChange = useCallback((checked: boolean) => {
    // 立即更新状态
    setHideOtherPairs(checked);
    // 立即在前端过滤数据
    filterPositionsByHideOtherPairs(checked, allPositions, allPositionsRawData);
    filterOrdersByHideOtherPairs(checked, allOrders);
  }, [allPositions, allPositionsRawData, allOrders, filterPositionsByHideOtherPairs, filterOrdersByHideOtherPairs]);

  // 当登录状态或当前交易对变化时，重新获取数据（不依赖 hideOtherPairs）
  useEffect(() => {
    if (!isLogin) {
      setPositions([]);
      setPositionsRawData([]);
      setAllPositions([]);
      setAllPositionsRawData([]);
      return;
    }

    query.current.page = 1;
    // 始终获取所有数据，不传 symbol 参数
    query.current.symbol = undefined;

    handleRefresh();
  }, [isLogin, currentSymbol, handleRefresh, router.pathname, router.asPath, router.query]);

  // 当切换到 position tab 时，重新获取持仓数据
  useEffect(() => {
    if (activeTab === "position" && isLogin && currentSymbol) {
      handleRefresh();
    }
  }, [activeTab, isLogin, currentSymbol, handleRefresh]);

  // 响应 store.trade.cfdPositionClosedPositionId（由 useUserLogin 的 private 通道 cfd_position_closed 统一写入）
  useEffect(() => {
    if (!cfdPositionClosedPositionId) return;
    const idStr = cfdPositionClosedPositionId;
    setAllPositions(prev => prev.filter(p => String(p.id) !== idStr));
    setAllPositionsRawData(prev => prev.filter((r: any) => String(r?.id) !== idStr));
    setPositions(prev => {
      const next = prev.filter(p => String(p.id) !== idStr);
      setPositionCount(next.length);
      return next;
    });
    setPositionsRawData(prev => prev.filter((r: any) => String(r?.id) !== idStr));
    store.trade.updateState({ cfdPositionClosedPositionId: undefined });
  }, [cfdPositionClosedPositionId]);

  // 仓位列表轮询：仅做列表一致性兜底（新开/平仓/数量变化），价格与收益由 socket ticker 实时更新
  useEffect(() => {
    if (!isLogin) return;
    const timer = setInterval(() => handleRefresh(false), POSITION_POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [isLogin, handleRefresh]);

  // 获取当前委托列表
  const fetchOrders = useCallback(async (showLoading = true) => {
    if (!isLogin) {
      setOrders([]);
      setOrderCount(0);
      return;
    }

    if (showLoading) {
      setOrdersLoading(true);
    }
    try {
      const params: any = {
        page: 1,
        limit: 100,
        propTrading: isPropTradingCfdTradeRoute(router),
      };

      // 始终获取所有数据，不传 symbol 参数（前端过滤）

      const { err, data } = await Request.XtAxios.asyncAwait(get_cfd_orders, {
        params,
      });
      if (err || !data) {
        setOrders([]);
        setOrderCount(0);
        return;
      }

      // API 返回结构: { rc: 0, mc: "", ma: [], result: { hasPrev: false, hasNext: false, items: [...] } }
      // data 已经是 result 层级
      const items = data?.items || [];
      
      // 更新订单数量
      const total = items.length;
      setOrderCount(total);
      
      // 将 API 返回的数据转换为组件需要的格式
      const formattedOrders = items.map((item: any) => {
        const quantity = Big(item.quantity || 0);
        const price = Big(item.price || 0);
        const fillPrice = Big(item.fillPrice || 0);
        const fee = Big(item.fee || 0);
        
        // 格式化交易对显示，去掉 _cfd 后缀，使用和现货一样的格式
        const pairDisplay = store.market.formatCfdName(item.symbol || "");
        
        // 获取精度
        const quantityPrecision = coinQuantityPrecisionMarket && coinQuantityPrecisionMarket > 0 ? coinQuantityPrecisionMarket : 8;
        const pricePrecision = coinPricePrecisionMarket && coinPricePrecisionMarket > 0 ? coinPricePrecisionMarket : 2;
        
        // 状态：1=待处理, 2=已匹配, 3=已完成, 4=已取消
        const getStatusText = (status: number) => {
          switch (status) {
            case 1:
              return t("cfd.pending");
            case 2:
              return t("cfd.partialFilled");
            case 3:
              return t("cfd.filled");
            case 4:
              return t("cfd.canceled");
            default:
              return t("cfd.none");
          }
        };
        
        // 价格类型：1=限价, 2=市价
        const priceTypeText = item.priceType === 1 ? t("cfd.limit") : item.priceType === 2 ? t("cfd.market") : t("cfd.none");
        
        // 订单类型：1=开仓, 2=平仓
        const orderTypeText = item.orderType === 1 ? t("cfd.openPosition") : item.orderType === 2 ? t("cfd.closePosition") : t("cfd.none");
        
        return {
          id: item.id,
          pair: pairDisplay,
          side: item.side === 1 ? "buy" : "sell", // 1=买涨, 2=卖跌
          leverage: `${item.leverage || 0}X`,
          quantity: quantity.gt(0) ? quantity.toFixedCy(quantityPrecision) : "0",
          price: price.gt(0) ? price.toFixedCy(pricePrecision) : t("cfd.market"),
          fillPrice: fillPrice.gt(0) ? fillPrice.toFixedCy(pricePrecision) : t("cfd.none"),
          fee: fee.gt(0) ? fee.toFixedCy(pricePrecision) : "0",
          status: getStatusText(item.status),
          statusCode: item.status, // 保留原始状态码，用于判断是否可以撤单
          priceType: priceTypeText,
          orderType: orderTypeText,
          createdTime: item.createdTime ? moment(item.createdTime).format("YY.MM.DD HH:mm:ss") : t("cfd.none"),
          updatedTime: item.updatedTime ? moment(item.updatedTime).format("YY.MM.DD HH:mm:ss") : t("cfd.none"),
          // 保留原始数据，方便后续操作
          rawData: item,
        };
      });

      // 保存所有订单数据（不过滤）
      setAllOrders(formattedOrders);
      
      // 根据 hideOtherPairs 过滤订单数据
      if (hideOtherPairs && currentSymbol) {
        // 过滤出当前交易对的订单
        const filtered = formattedOrders.filter((order) => {
          const rawItem = order.rawData;
          if (!rawItem || !rawItem.symbol) return false;
          const itemSymbol = rawItem.symbol.replace(/_cfd$/i, "").toUpperCase();
          return itemSymbol === currentSymbol;
        });
        setOrders(filtered);
        setOrderCount(filtered.length);
      } else {
        // 显示所有订单
        setOrders(formattedOrders);
        setOrderCount(formattedOrders.length);
      }
    } catch (err) {
      console.error("【获取当前委托列表失败】", err);
      setOrders([]);
      setAllOrders([]);
      setOrderCount(0);
    } finally {
      if (showLoading) {
        setOrdersLoading(false);
      }
    }
  }, [isLogin, currentSymbol, hideOtherPairs, coinQuantityPrecisionMarket, coinPricePrecisionMarket, t, router]);

  // 页面加载时获取订单数量（不显示 loading）
  useEffect(() => {
    if (isLogin) {
      fetchOrders(false);
    }
  }, [isLogin, fetchOrders]);

  // 当切换到 order tab 时，重新获取委托数据
  // 当 hideOtherPairs 变化时，fetchOrders 会重新创建，这个 useEffect 会自动重新执行
  useEffect(() => {
    if (activeTab === "order" && isLogin) {
      fetchOrders();
    }
  }, [activeTab, isLogin, fetchOrders]);

  // 暴露刷新方法给父组件：同时拉持仓 + 当前委托（与 trade 页下单成功后的静默刷新约定一致）
  useImperativeHandle(ref, () => ({
    refresh: (showLoading?: boolean) => {
      const show = showLoading !== false;
      handleRefresh(show);
      fetchOrders(show);
    },
  }), [handleRefresh, fetchOrders]);

  const handleCancelOrder = useCallback((orderId: string) => {
    setCloseConfirmContent(t("cfd.cancelOrderConfirm") || "Confirm to cancel this order?");
    closeConfirmCallbackRef.current = async () => {
      try {
        const { err } = await Request.XtAxios.asyncAwait(() => 
          post_cfd_cancelOrder(orderId)
        );

        if (err) {
          message.error(err.msg || t("cfd.cancelOrderFailed") || "Cancel order failed");
          return;
        }

        // 撤单成功，刷新订单列表
        message.success(t("cfd.cancelOrderSuccess") || "Order canceled successfully");
        fetchOrders();
        setCloseConfirmOpen(false);
      } catch (error) {
        console.error("撤单失败:", error);
        message.error(t("cfd.cancelOrderFailed") || "Cancel order failed");
      } finally {
        setCloseConfirmLoading(false);
      }
    };
    setCloseConfirmOpen(true);
  }, [t, fetchOrders]);

  const handleOvernightChange = useCallback((positionId: string, checked: boolean) => {
    const confirmText = checked 
      ? t("cfd.enableOvernightConfirm") || "Enable overnight?"
      : t("cfd.disableOvernightConfirm") || "Disable overnight?";
    
    ModalAlert.confirm({
      content: confirmText,
      okText: t("confirm") || "Confirm",
      cancelText: t("cancel") || "Cancel",
      onOk: async (close) => {
        close();
        
        // 使用函数式更新，在更新前保存当前状态用于还原
        let previousPositions: any[] = [];
        let previousPositionsRawData: any[] = [];
        let previousAllPositions: any[] = [];
        let previousAllPositionsRawData: any[] = [];
        
        // 更新 positions 中的状态（使用函数式更新确保使用最新值）
        setPositions(prev => {
          previousPositions = [...prev];
          return prev.map(pos => 
            pos.id === positionId ? { ...pos, overnight: checked } : pos
          );
        });
        
        // 更新 allPositions 中的状态
        setAllPositions(prev => {
          previousAllPositions = [...prev];
          return prev.map(pos => 
            pos.id === positionId ? { ...pos, overnight: checked } : pos
          );
        });
        
        // 更新 positionsRawData 中的状态（需要根据 positionId 找到对应的 rawData）
        setPositionsRawData(prev => {
          previousPositionsRawData = [...prev];
          // 在 previousPositions 中找到对应的 position 索引
          const positionIndex = previousPositions.findIndex(pos => pos.id === positionId);
          if (positionIndex >= 0 && positionIndex < prev.length && prev[positionIndex]) {
            return prev.map((raw, index) => 
              index === positionIndex ? { ...raw, keep: checked ? 1 : 0 } : raw
            );
          }
          return prev;
        });
        
        // 更新 allPositionsRawData 中的状态
        setAllPositionsRawData(prev => {
          previousAllPositionsRawData = [...prev];
          // 在 previousAllPositions 中找到对应的 position 索引
          const positionIndex = previousAllPositions.findIndex(pos => pos.id === positionId);
          if (positionIndex >= 0 && positionIndex < prev.length && prev[positionIndex]) {
            return prev.map((raw, index) => 
              index === positionIndex ? { ...raw, keep: checked ? 1 : 0 } : raw
            );
          }
          return prev;
        });
        
        try {
          const { err } = await Request.XtAxios.asyncAwait(() => 
            put_cfd_positionKeep(positionId, {
              data: { keep: checked ? 1 : 0 }, // 0=关闭, 1=开启
            })
          );

          if (err) {
            // 接口失败，还原状态
            setPositions(previousPositions);
            setPositionsRawData(previousPositionsRawData);
            setAllPositions(previousAllPositions);
            setAllPositionsRawData(previousAllPositionsRawData);
            message.error(err.msg || t("cfd.updateFailed"));
            return;
          }

          // 更新成功，不刷新列表
          message.success(t("cfd.updateSuccess"));
        } catch (error) {
          // 接口失败，还原状态
          setPositions(previousPositions);
          setPositionsRawData(previousPositionsRawData);
          setAllPositions(previousAllPositions);
          setAllPositionsRawData(previousAllPositionsRawData);
          console.error("更新持仓过夜状态失败:", error);
          message.error(t("cfd.updateFailed"));
        }
      },
    });
  }, [t]);

  const handleClosePosition = useCallback((position: any) => {
    const needConfirm = store.trade.closeConfirm_position !== false;
    const submitClosePosition = async () => {
      try {
        const { err } = await Request.XtAxios.asyncAwait(() =>
          post_cfd_closePosition({
            data: { positionId: Number(position.id) },
          }),
        );

        if (err) {
          message.error(err.msg || t("cfd.updateFailed") || "Close position failed");
          return;
        }

        message.success(t("cfd.closePositionSuccess") || "Close position successful");
        handleRefresh();
        setCloseConfirmOpen(false);
      } catch (error) {
        console.error("平仓失败:", error);
        message.error(t("cfd.updateFailed") || "Close position failed");
      } finally {
        setCloseConfirmLoading(false);
      }
    };
    if (!needConfirm) {
      setCloseConfirmLoading(true);
      submitClosePosition();
      return;
    }
    closeConfirmSkipNextCheckedRef.current = false;
    setCloseConfirmSkipNextChecked(false);
    setShowCloseConfirmSkipNext(true);
    setCloseConfirmContent(t("cfd.closePositionConfirm"));
    closeConfirmCallbackRef.current = async () => {
      if (closeConfirmSkipNextCheckedRef.current) {
        store.trade.updateState({ closeConfirm_position: false });
        Storage.set("closeConfirm_position", false);
      }
      await submitClosePosition();
    };
    setCloseConfirmOpen(true);
  }, [t, handleRefresh]);

  const handleCloseAll = useCallback(() => {
    if (!loading && allPositions.length === 0) {
      message.info(t("cfd.noPositions"));
      return;
    }
    const needConfirm = store.trade.closeConfirm_position !== false;
    const submitCloseAll = async () => {
      try {
        const { err } = await Request.XtAxios.asyncAwait(() => post_cfd_closeAllPositions());

        if (err) {
          message.error(err.msg || t("cfd.updateFailed") || "Close all positions failed");
          return;
        }

        message.success(t("cfd.closeAllPositionsSuccess") || "All positions closed successfully");
        handleRefresh();
        setCloseConfirmOpen(false);
      } catch (error) {
        console.error("一键平仓失败:", error);
        message.error(t("cfd.updateFailed") || "Close all positions failed");
      } finally {
        setCloseConfirmLoading(false);
      }
    };
    if (!needConfirm) {
      setCloseConfirmLoading(true);
      submitCloseAll();
      return;
    }
    closeConfirmSkipNextCheckedRef.current = false;
    setCloseConfirmSkipNextChecked(false);
    setShowCloseConfirmSkipNext(true);
    setCloseConfirmContent(t("cfd.closeAllPositionsConfirm"));
    closeConfirmCallbackRef.current = async () => {
      if (closeConfirmSkipNextCheckedRef.current) {
        store.trade.updateState({ closeConfirm_position: false });
        Storage.set("closeConfirm_position", false);
      }
      await submitCloseAll();
    };
    setCloseConfirmOpen(true);
  }, [t, handleRefresh, loading, allPositions]);

  const handleCancelAll = useCallback(() => {
    if (!ordersLoading && allOrders.length === 0) {
      message.info(t("cfd.noOrders"));
      return;
    }

    closeConfirmSkipNextCheckedRef.current = false;
    setCloseConfirmSkipNextChecked(false);
    setShowCloseConfirmSkipNext(false);
    setCloseConfirmContent(t("cfd.cancelAllOrdersConfirm"));
    closeConfirmCallbackRef.current = async () => {
      try {
        const { err } = await Request.XtAxios.asyncAwait(() => 
          post_cfd_cancelAllOrders()
        );

        if (err) {
          message.error(err.msg || t("cfd.updateFailed") || "Cancel all orders failed");
          return;
        }

        message.success(t("cfd.cancelAllOrdersSuccess") || "All orders canceled successfully");
        fetchOrders();
        setCloseConfirmOpen(false);
      } catch (error) {
        console.error("取消所有订单失败:", error);
        message.error(t("cfd.updateFailed") || "Cancel all orders failed");
      } finally {
        setCloseConfirmLoading(false);
      }
    };
    setCloseConfirmOpen(true);
  }, [t, fetchOrders, ordersLoading, allOrders]);


  return (
    <div className={styles.positionList}>
      {/* 标签页 */}
      <div className={styles.tabs} data-guide-target="positionListTabs">
        <div className={styles.tabLeft}>
          <button
            className={cx(styles.tab, { [styles.active]: activeTab === "position" })}
            onClick={() => setActiveTab("position")}
          >
            {t("cfd.currentPositions")}({positionCount})
          </button>
          <button
            className={cx(styles.tab, { [styles.active]: activeTab === "order" })}
            onClick={() => setActiveTab("order")}
          >
            {t("cfd.currentOrders")}({orderCount})
          </button>
        </div>
        <button
          type="button"
          className={styles.viewOrderBtn}
          data-guide-target="historyButton"
          onClick={() => {
            const symbol = store.market.name || "btc_usdt";
            const fromProp =
              (router.pathname || "") === "/proptrading/trade/[symbol]" ||
              (router.pathname || "") === "/proptrading/trade";
            const qs = fromProp ? "?propTrading=true" : "";
            router.push(`/futures/record/${symbol}${qs}`);
          }}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
            <rect x="1" y="1" width="14" height="14" rx="2.46094" stroke="white" strokeWidth="1.7"/>
            <path d="M5 6H11" stroke="white" strokeWidth="1.6" strokeLinecap="round"/>
            <path d="M5 10H11" stroke="white" strokeWidth="1.6" strokeLinecap="round"/>
          </svg>
        </button>
      </div>

      {/* 操作栏 */}
      <div className={styles.actions}>
        <Checkbox className={styles.checkbox} checked={hideOtherPairs} onChange={(e) => handleHideOtherPairsChange(e.target.checked)}>
          {t("trade.hideOtherSymbol")}
        </Checkbox>
        {activeTab === "position" && (
        <button className={styles.closeAllBtn} onClick={handleCloseAll}>
          {t("cfd.closeAll")}
        </button>
        )}
        {activeTab === "order" && (
          <button className={styles.closeAllBtn} onClick={handleCancelAll}>
            {t("cfd.cancelAll") || "Cancell All"}
          </button>
        )}
      </div>

      {/* 持仓列表 */}
      {activeTab === "position" && (
        <div className={styles.positions}>
          {loading ? (
            <div className={styles.empty}>{t("cfd.loading")}</div>
          ) : positions.length === 0 ? (
            <div className={styles.empty}>{t("cfd.noPositions")}</div>
          ) : (
            positions.map((position, index) => (
              <PositionItem
                key={position.id}
                position={position}
                rawData={positionsRawData[index]}
                t={t}
                onPairClick={handlePairClick}
                handleOvernightChange={handleOvernightChange}
                setStopPosition={setStopPosition}
                setStopModalOpen={setStopModalOpen}
                handleClosePosition={handleClosePosition}
                coinPricePrecisionMarket={coinPricePrecisionMarket}
                getRealTimePrice={getRealTimePrice}
                expanded={expandedPositionIds.has(position.id)}
                onToggleExpand={() => togglePositionExpand(position.id)}
                onShareClick={(payload) => {
                  setSharePayload(payload);
                  setShareModalOpen(true);
                }}
              />
            ))
          )}
        </div>
      )}

      {activeTab === "order" && (
        <div className={styles.orders}>
          {ordersLoading ? (
            <div className={styles.empty}>{t("cfd.loading")}</div>
          ) : orders.length === 0 ? (
            <div className={styles.empty}>{t("cfd.noOrders")}</div>
          ) : (
            orders.map((order) => (
              <div key={order.id} className={styles.orderItem}>
                {/* 第一行：左侧信息 */}
                <div className={styles.orderHeader}>
                  <div className={styles.orderInfo}>
                    <span className={cx(styles.sideTag, { [styles.buy]: order.side === "buy", [styles.sell]: order.side === "sell" })}>
                      {order.side === "buy" ? t("cfd.buyUp") : t("cfd.buyDown")}
                    </span>
                    <span className={styles.pair}>{order.pair}</span>
                    <span className={styles.leverage}>{order.leverage}</span>
                  </div>
                  <span className={styles.status}>{order.status}</span>
                </div>

                {/* 订单详情 */}
                <div className={styles.orderDetails}>
                  <div className={styles.detailRow}>
                    <div className={styles.detailItem}>
                      <span className={styles.detailLabel}>{t("cfd.orderType")}</span>
                      <span className={styles.detailValue}>{order.orderType}</span>
                    </div>
                    <div className={styles.detailItem}>
                      <span className={styles.detailLabel}>{t("cfd.type")}</span>
                      <span className={styles.detailValue}>{order.priceType}</span>
                    </div>
                    <div className={styles.detailItem}>
                      <span className={styles.detailLabel}>{t("cfd.orderQuantity")}</span>
                      <span className={styles.detailValue}>{order.quantity}</span>
                    </div>
                  </div>
                  <div className={styles.detailRow}>
                    <div className={styles.detailItem}>
                      <span className={styles.detailLabel}>{t("cfd.orderPrice")}</span>
                      <span className={styles.detailValue}>{order.price}</span>
                    </div>
                    <div className={styles.detailItem}>
                      <span className={styles.detailLabel}>{t("cfd.avgFillPrice")}</span>
                      <span className={styles.detailValue}>{order.fillPrice}</span>
                    </div>
                    <div className={styles.detailItem}>
                      <span className={styles.detailLabel}>{t("cfd.fee")}</span>
                      <span className={styles.detailValue}>{order.fee}</span>
                    </div>
                  </div>
                  <div className={styles.detailRow}>
                    <div className={styles.detailItem}>
                      <span className={styles.detailLabel}>{t("cfd.orderTime")}</span>
                      <span className={styles.detailValue}>{order.createdTime}</span>
                    </div>
                    <div className={styles.detailItem}>
                      <span className={styles.detailLabel}>{t("cfd.updateTime")}</span>
                      <span className={styles.detailValue}>{order.updatedTime}</span>
                    </div>
                  </div>
                </div>

                {/* 撤单按钮 - 只有待处理(1)或部分成交(2)的订单才能撤单 */}
                {(order.statusCode === 1 || order.statusCode === 2) && (
                  <div className={styles.orderActions}>
                    <button
                      className={cx(styles.actionBtn, styles.cancelBtn)}
                      onClick={() => handleCancelOrder(order.id)}
                    >
                      {t("cfd.cancelOrder") || "Cancel Order"}
                    </button>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}

      <StopModal 
        open={stopModalOpen} 
        onClose={() => setStopModalOpen(false)} 
        onSuccess={handleRefresh}
        position={stopPosition} 
      />

      <CloseConfirmModal
        open={closeConfirmOpen}
        onClose={() => {
          setCloseConfirmOpen(false);
          closeConfirmCallbackRef.current = null;
          setCloseConfirmLoading(false);
          closeConfirmSkipNextCheckedRef.current = false;
          setCloseConfirmSkipNextChecked(false);
          setShowCloseConfirmSkipNext(false);
        }}
        onConfirm={async () => {
          if (closeConfirmCallbackRef.current) {
            setCloseConfirmLoading(true);
            try {
              await closeConfirmCallbackRef.current();
            } catch (error) {
              console.error("确认操作失败:", error);
              setCloseConfirmLoading(false);
            }
          }
        }}
        content={closeConfirmContent}
        loading={closeConfirmLoading}
        showSkipNext={showCloseConfirmSkipNext}
        skipNextChecked={closeConfirmSkipNextChecked}
        onSkipNextChange={(checked) => {
          closeConfirmSkipNextCheckedRef.current = checked;
          setCloseConfirmSkipNextChecked(checked);
        }}
      />

      <SharePosterModal
        open={shareModalOpen}
        onClose={() => {
          setShareModalOpen(false);
          setSharePayload(null);
        }}
        payload={sharePayload}
      />
    </div>
  );
});

PositionList.displayName = "PositionList";

export default observer(PositionList);
